"use client"

import { useState, useEffect, useRef } from "react"
import { Upload, RefreshCw, CheckCircle, FileSpreadsheet } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/Dialog"
import { Button } from "./ui/Button"
import { analyzeExcelFile, startImportJob, getImportJobStatus, commitImportJob } from "../api/importJobs"
import { useToast } from "../hooks/useToast"

/**
 * Import wizard modal component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Is modal open
 * @param {Function} props.onClose - Close callback
 * @param {string} props.token - Auth token
 * @param {Function} props.onComplete - Complete callback
 */
const ImportWizardModal = ({ open, onClose, token, onComplete }) => {
  // Wizard state
  const [step, setStep] = useState("upload") // upload, processing, summary
  const [file, setFile] = useState(null)
  const [status, setStatus] = useState("")
  const [error, setError] = useState("")
  const [jobId, setJobId] = useState(null)
  const [summary, setSummary] = useState(null)
  const [previewData, setPreviewData] = useState([])
  // Toast for user feedback
  const { toast } = useToast()

  // Polling reference
  const pollInterval = useRef(null)
  const pollTimeout = useRef(null)

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      // Clean up on close
      if (pollInterval.current) {
        clearInterval(pollInterval.current)
      }
      if (pollTimeout.current) {
        clearTimeout(pollTimeout.current)
      }

      // Reset state
      setStep("upload")
      setFile(null)
      setStatus("")
      setError("")
      setJobId(null)
      setSummary(null)
    }
  }, [open]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current)
      }
      if (pollTimeout.current) {
        clearTimeout(pollTimeout.current)
      }
    }
  }, [])

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      setFile(selectedFile)
      setError("")
    }
  }

  const startImport = async () => {
    if (!file) {
      setError("Please select a file")
      return
    }

    try {
      setStep("processing")
      setStatus("Analyzing file...")
      setError("")

      // Step 1: Analyze file
      const { guessedMapping } = await analyzeExcelFile(token, file)

      // Step 2: Start import job
      setStatus("Starting import job...")
      const { jobId } = await startImportJob(token, file, guessedMapping)
      setJobId(jobId)

      // Step 3: Poll for status
      setStatus("Processing file...")
      startPolling(jobId)
    } catch (err) {
      console.error("Import error:", err)
      setError(err.message || "Failed to start import")
      setStep("upload")
    }
  }

  const startPolling = (jid) => {
    // Set a timeout to stop polling after 60 seconds
    pollTimeout.current = setTimeout(() => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current)
        setError("Import timed out. Please try again.")
        setStep("upload")
      }
    }, 60000)

    // Poll every 1.5 seconds
    pollInterval.current = setInterval(async () => {
      try {
        const job = await getImportJobStatus(token, jid)

        if (job.status === "preview") {
          // Stop polling
          clearInterval(pollInterval.current)
          clearTimeout(pollTimeout.current)
          // Move to verify step with preview data
          setPreviewData(job.preview || [])
          setStep("verify")
        } else if (job.status === "failed") {
          // Stop polling
          clearInterval(pollInterval.current)
          clearTimeout(pollTimeout.current)

          setError(job.error || "Import failed")
          setStep("upload")
        }
      } catch (err) {
        console.error("Polling error:", err)
        clearInterval(pollInterval.current)
        clearTimeout(pollTimeout.current)

        setError(err.message || "Failed to check import status")
        setStep("upload")
      }
    }, 1500)
  }

  const handleComplete = () => {
    onComplete()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 text-white border border-gray-700 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Cost Codes</DialogTitle>
        </DialogHeader>

        {step === "upload" && (
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer border-gray-700 bg-gray-800 hover:bg-gray-750">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <FileSpreadsheet className="w-10 h-10 mb-3 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-400">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">Excel files only (.xlsx, .xls)</p>
                </div>
                <input type="file" className="hidden" accept=".xlsx,.xls" onChange={handleFileChange} />
              </label>
            </div>

            {file && (
              <div className="flex items-center p-2 bg-gray-800 rounded">
                <FileSpreadsheet className="w-5 h-5 mr-2 text-blue-500" />
                <span className="text-sm truncate">{file.name}</span>
              </div>
            )}

            {error && <div className="p-2 text-sm text-red-400 bg-red-900 bg-opacity-30 rounded">{error}</div>}
          </div>
        )}

        {step === "processing" && (
          <div className="py-8 flex flex-col items-center justify-center">
            <RefreshCw className="w-12 h-12 mb-4 text-blue-500 animate-spin" />
            <p className="text-lg font-medium mb-2">{status}</p>
            <p className="text-sm text-gray-400">This may take a few moments...</p>
          </div>
        )}
        {/* Verification step: preview and edit parsed cost codes */}
        {step === "verify" && (
          <div className="py-4 space-y-4">
            <h3 className="text-lg font-semibold">Review Parsed Cost Codes</h3>
            <div className="space-y-6 max-h-96 overflow-y-auto">
              {Object.entries(
                previewData.reduce((map, c) => {
                  if (!map[c.category]) map[c.category] = [];
                  map[c.category].push(c);
                  return map;
                }, {})
              ).map(([category, items]) => (
                <div key={category}>
                  <div className="inline-block px-3 py-1 bg-blue-600 text-white rounded-full mb-2">
                    {category} ({items.length})
                  </div>
                  <table className="w-full text-sm text-left text-white mb-4">
                    <thead className="text-xs uppercase bg-gray-800 text-gray-400">
                      <tr>
                        <th className="px-2 py-1">Code</th>
                        <th className="px-2 py-1">Description</th>
                        <th className="px-2 py-1">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((c, idx) => (
                        <tr key={`${category}-${c.code}-${idx}`} className="odd:bg-gray-900 even:bg-gray-850">
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              className="w-full p-1 bg-gray-800 text-white rounded"
                              value={c.code}
                              onChange={e => {
                                const newCode = e.target.value;
                                setPreviewData(prev => {
                                  const copy = [...prev];
                                  const flatIndex = prev.findIndex(x => x === c);
                                  copy[flatIndex] = { ...c, code: newCode };
                                  return copy;
                                });
                              }}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              className="w-full p-1 bg-gray-800 text-white rounded"
                              value={c.description}
                              onChange={e => {
                                const newDesc = e.target.value;
                                setPreviewData(prev => {
                                  const copy = [...prev];
                                  const flatIndex = prev.findIndex(x => x === c);
                                  copy[flatIndex] = { ...c, description: newDesc };
                                  return copy;
                                });
                              }}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white"
                              onClick={() => {
                                setPreviewData(prev => prev.filter(x => x !== c));
                              }}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-4">
              <Button
                variant="outline"
                onClick={() => setPreviewData(prev => [...prev, { category: '', code: '', description: '' }])}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Add Cost Code
              </Button>
              <Button
                onClick={async () => {
                  setStatus("Committing changes...");
                  try {
                    const result = await commitImportJob(token, jobId, previewData);
                    setSummary(result);
                    setStep("summary");
                  } catch (err) {
                    console.error('Commit import error:', err);
                    const statusCode = err.response?.status;
                    const serverMsg = err.response?.data?.message;
                    if (statusCode === 400 && serverMsg?.match(/already in use/)) {
                      setError(serverMsg);
                      toast({ description: serverMsg, variant: 'destructive' });
                    } else if (serverMsg) {
                      setError(serverMsg);
                      toast({ description: serverMsg, variant: 'destructive' });
                    } else {
                      const fallback = 'Commit failed. Please try again.';
                      setError(fallback);
                      toast({ description: fallback, variant: 'destructive' });
                    }
                    setStep('verify');
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Commit Import
              </Button>
            </div>
          </div>
        )}

        {step === "summary" && summary && (
          <div className="py-4 space-y-4">
            <div className="bg-gray-800 p-4 rounded">
              <h3 className="text-lg font-medium mb-4 text-center">Import Summary</h3>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-750 p-3 rounded">
                  <p className="text-sm text-gray-400">Inserted</p>
                  <p className="text-2xl font-bold text-green-500">{summary.inserted}</p>
                </div>

                <div className="bg-gray-750 p-3 rounded">
                  <p className="text-sm text-gray-400">Updated</p>
                  <p className="text-2xl font-bold text-blue-500">{summary.updated}</p>
                </div>

                <div className="bg-gray-750 p-3 rounded">
                  <p className="text-sm text-gray-400">Deactivated</p>
                  <p className="text-2xl font-bold text-yellow-500">{summary.muted}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <CheckCircle className="w-6 h-6 mr-2 text-green-500" />
              <p className="text-green-400">Import completed successfully</p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "upload" && (
            <>
              <Button
                variant="outline"
                onClick={onClose}
                className="bg-gray-800 border-gray-700 hover:bg-gray-700 text-white"
              >
                Cancel
              </Button>
              <Button onClick={startImport} disabled={!file} className="bg-green-600 hover:bg-green-700 text-white">
                <Upload className="h-4 w-4 mr-2" />
                Start Import
              </Button>
            </>
          )}

          {step === "summary" && (
            <Button onClick={handleComplete} className="bg-blue-600 hover:bg-blue-700 text-white">
              Close & Refresh
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ImportWizardModal
